import { FormControlLabel, Grid2, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import React, { useEffect, useState } from "react";
import {
  COLUMNS_PERSIST,
  generateColumnVisibilityState,
  resetColumns,
} from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { GKModalProps } from "utils/commonInterface";
import GKButton from "./GKButton";
import GKCheckbox from "./GKCheckbox";
import GKModal from "./GKModal";
import { ResetIcon } from "./Icons";

interface Columns {
  [key: string]: boolean;
}

interface TableColumnModalProps {
  name: string;
  columns: readonly GridColDef[];
  columnVisibilityModel: {
    [key: string]: Columns;
  };
  disabledColumns?: string[];
}

const TableColumnModal = (props: TableColumnModalProps & GKModalProps) => {
  const {
    open,
    setOpen,
    columns,
    columnVisibilityModel,
    name,
    disabledColumns,
  } = props;

  const [localVisibility, setLocalVisibility] = useState<Columns>({});

  const dispatch = useAppDispatch();
  const currentColumnVisibility = useAppSelector(
    (state) => state?.table?.columnsPersist?.[name],
  );

  useEffect(() => {
    if (columns?.length > 0 && open) {
      const visibility =
        currentColumnVisibility ||
        generateColumnVisibilityState(
          columns.map((col: { field: string }) => col.field),
        );

      setLocalVisibility(visibility);
    }
  }, [columns, currentColumnVisibility, open]);

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setLocalVisibility((prev) => ({ ...prev, [field]: checked }));
  };

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={"Column Customization"}
      subTitle={
        <Typography fontSize={12} fontWeight={500} color="#697586">
          {Object.values(localVisibility || {})?.filter(Boolean)?.length || 0}{" "}
          out of {columns?.length} Enabled
        </Typography>
      }
      width={"350px"}
      footer={
        <Grid2 container size={12} spacing={1}>
          <Grid2 size={6}>
            <GKButton
              fullWidth
              color="dark"
              variant="outlined"
              startIcon={<ResetIcon />}
              onClick={() => {
                dispatch(resetColumns(name));
                setOpen(false);
              }}
            >
              Restore Default
            </GKButton>
          </Grid2>
          <Grid2 size={6}>
            <GKButton
              fullWidth
              variant="contained"
              onClick={() => {
                const hasChanged = Object.values(localVisibility).some(
                  (v) => v === false,
                );

                dispatch(
                  COLUMNS_PERSIST({
                    ...columnVisibilityModel,
                    [name]: hasChanged ? localVisibility : undefined,
                  }),
                );
                setOpen(false);
              }}
            >
              Apply Columns
            </GKButton>
          </Grid2>
        </Grid2>
      }
    >
      <Grid2 container>
        {columns?.map((column: GridColDef) => {
          const isChecked = localVisibility?.[column?.field] ?? true;

          return (
            <Grid2
              size={12}
              key={column.field}
              sx={{
                ":hover": { borderRadius: "6px", backgroundColor: "#EEF5FA" },
              }}
            >
              <FormControlLabel
                control={
                  <GKCheckbox
                    checked={isChecked}
                    disabled={disabledColumns?.includes(column?.field)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleCheckboxChange(column.field, event.target.checked)
                    }
                    sx={{ padding: "14px" }}
                  />
                }
                label={column?.headerName}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "13px",
                    fontWeight: 400,
                  },
                  "&.Mui-disabled .MuiTypography-root": {
                    color: "var(--grey-700)",
                    fontWeight: 500,
                  },
                }}
              />
            </Grid2>
          );
        })}
      </Grid2>
    </GKModal>
  );
};

export default TableColumnModal;
